import styles from "./map-modal.module.css";
import "maplibre-gl/dist/maplibre-gl.css"; // See notes below
import { useCallback, useEffect, useState } from "react";
import { Input, Modal, Flex, Button, List, Typography, Spin } from "antd";
import {
  Map,
  FullscreenControl,
  GeolocateControl,
  Marker,
  NavigationControl,
} from "@vis.gl/react-maplibre";

import SearchIcon from "@/icons/Search";

import { getLocationText, searchLocation } from "@/api/map";

import { DEFAULT_VIEW_STATE, MAP_STYLES } from "@/constants/map";

// Окно
const MapModal = ({ data = {}, visible, onSave, onCancel }) => {
  // Контролируем текущее положение карты
  const [viewState, setViewState] = useState(DEFAULT_VIEW_STATE);
  // Строка поиска и результаты геокодера
  const [address, setAddress] = useState("");

  const [searchResults, setSearchResults] = useState([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const [searchPerformed, setSearchPerformed] = useState(false);

  // Активный маркер на карте (отсутствует, пока пользователь не выбрал точку)
  const [marker, setMarker] = useState(null);
  // Показываем глобальный лоадер во время обратного геокодирования
  const [reverseGeocoding, setReverseGeocoding] = useState(false);

  // Чистим результаты поиска и флаг выполненного запроса
  const resetSearchState = useCallback(() => {
    setSearchResults([]);
    setSearchPerformed(false);
  }, []);

  // Обновляем геометку и необязательно центрируем карту
  const applyCoordinates = useCallback(
    ({ longitude, latitude, zoom, shouldCenter = false }) => {
      if (shouldCenter) {
        setViewState((prev) => ({
          ...prev,
          longitude,
          latitude,
          ...(typeof zoom === "number" ? { zoom } : {}),
        }));
      }

      setMarker({ longitude, latitude });
    },
    [],
  );

  // Сохраняем ввод пользователя в поле поиска
  const handleSearchChange = (e) => {
    setAddress(e.target.value);
  };

  // Поиск адресов через Nominatim
  const handleSearch = async (value) => {
    const query = value.trim();
    if (!query) {
      resetSearchState();
      return;
    }

    setSearchLoading(true);
    setSearchPerformed(true);
    try {
      const data = await searchLocation(query);
      setSearchResults(data);
    } catch (error) {
      setSearchResults([]);
    } finally {
      setSearchLoading(false);
    }
  };

  // Выбираем адрес из подсказок, центрируем карту и ставим маркер
  const handleSelectResult = (item) => {
    const lng = parseFloat(item.lon);
    const lat = parseFloat(item.lat);

    setAddress(item.display_name);
    applyCoordinates({
      longitude: lng,
      latitude: lat,
      zoom: 15,
      shouldCenter: true,
    });
    resetSearchState();
  };

  // Подставляем человекочитаемый адрес по координатам маркера
  const reverseGeocode = useCallback(async (location) => {
    try {
      const displayName = await getLocationText(location);
      if (displayName) {
        setAddress(displayName);
      }
    } catch (error) {
      // ignore reverse geocode failures
    }
  }, []);

  // Обрабатываем клики по карте и перетаскивания маркера
  const handleMarkerPositionUpdate = async (lng, lat) => {
    applyCoordinates({ longitude: lng, latitude: lat });
    resetSearchState();
    setReverseGeocoding(true);
    try {
      await reverseGeocode({ longitude: lng, latitude: lat });
    } finally {
      setReverseGeocoding(false);
    }
  };

  useEffect(() => {
    if (!visible) {
      return;
    }

    const resolveCoordinate = (...candidates) => {
      for (const candidate of candidates) {
        if (typeof candidate === "number" && Number.isFinite(candidate)) {
          return candidate;
        }
        if (typeof candidate === "string" && candidate.trim() !== "") {
          const parsed = parseFloat(candidate);
          if (Number.isFinite(parsed)) {
            return parsed;
          }
        }
      }
      return null;
    };

    const lat = resolveCoordinate(data?.latitude, data?.lat, data?.x);
    const lng = resolveCoordinate(
      data?.longitude,
      data?.lon,
      data?.lng,
      data?.y,
    );

    if (lat === null || lng === null) {
      return;
    }

    resetSearchState();
    applyCoordinates({
      longitude: lng,
      latitude: lat,
      zoom: 15,
      shouldCenter: true,
    });

    if (typeof data?.address === "string" && data.address.trim() !== "") {
      setAddress(data.address);
      return;
    }

    setReverseGeocoding(true);
    reverseGeocode({ longitude: lng, latitude: lat }).finally(() =>
      setReverseGeocoding(false),
    );
  }, [visible, data, applyCoordinates, resetSearchState, reverseGeocode]);

  const isDropdownVisible = searchResults.length > 0;
  const hasNoResults = searchPerformed && !searchLoading && !isDropdownVisible;

  return (
    <Modal
      maskClosable={false}
      title="Местоположение"
      open={visible}
      onOk={() => onSave(marker)}
      onCancel={onCancel}
      okText="Сохранить"
      cancelText="Отмена"
    >
      <Spin spinning={reverseGeocoding} tip="Определяем адрес...">
        <Flex vertical gap={10}>
          <div className={styles.searchContainer}>
            <Input.Search
              allowClear
              placeholder="Введите адрес"
              className={styles.searchInput}
              value={address}
              onChange={handleSearchChange}
              onSearch={handleSearch}
              loading={searchLoading}
              enterButton={
                <Button
                  type="primary"
                  loading={searchLoading}
                  icon={<SearchIcon style={{ fontSize: 14 }} />}
                ></Button>
              }
            />

            {isDropdownVisible && (
              <List
                size="small"
                bordered
                dataSource={searchResults}
                renderItem={(item) => (
                  <List.Item
                    onClick={() => handleSelectResult(item)}
                    style={{ cursor: "pointer" }}
                  >
                    {item.display_name}
                  </List.Item>
                )}
                className={styles.dropdown}
              />
            )}

            {hasNoResults && (
              <div className={styles.noResults}>
                <Typography.Text type="secondary">
                  Ничего не найдено
                </Typography.Text>
              </div>
            )}
          </div>

          <Map
            {...viewState}
            style={{ width: "100%", height: 400 }}
            mapStyle={MAP_STYLES.voyager}
            attributionControl={false}
            cursor={"pointer"}
            onMove={(evt) => setViewState(evt.viewState)}
            onClick={(evt) =>
              handleMarkerPositionUpdate(evt.lngLat.lng, evt.lngLat.lat)
            }
          >
            {/* Стандартные контролы карты */}
            <FullscreenControl />
            <GeolocateControl />
            <NavigationControl showCompass={false} />

            {marker && (
              <Marker
                longitude={marker.longitude}
                latitude={marker.latitude}
                anchor="bottom"
                offset={[0, 0]}
                draggable
                onDragEnd={(evt) =>
                  handleMarkerPositionUpdate(evt.lngLat.lng, evt.lngLat.lat)
                }
              />
            )}
          </Map>
        </Flex>
      </Spin>
    </Modal>
  );
};

export default MapModal;
